/**
 * Anfrage-Abbruch — Zeilen-/Kartenmenü (⋯) und Bestätigungs-Dialog.
 *
 * Menü: einzelner Punkt "Anfrage abbrechen" (rot). Dialog: Grund-Auswahl aus
 * dem admin-pflegbaren Katalog (GET /v2/admin/anfrage-abbruch-gruende);
 * Gründe mit requires_beschreibung (Basis: "Sonstiges") blenden ein
 * Pflicht-Freitextfeld ein. Bestätigen → POST /users/{id}/anfrage-abbruch,
 * die Anfrage fällt aus der Pipeline (phase wird negiert).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { v2AbbruchGruende, v2AnfrageAbbruch } from '../../lib/v1';
import type { AbbruchGrund, AbbruchScope, AnfrageAbbruchResult } from '../../lib/v1';
import { usePermissions } from '../../contexts/PermissionsContext';
import { toast } from '../../lib/toast';

/* ─── Zeilen-Menü (⋯) ────────────────────────────────────────────────────── */

interface AnfrageActionsMenuProps {
  dark: boolean;
  onAbbrechen: () => void;
}

export function AnfrageActionsMenu({ dark, onAbbrechen }: AnfrageActionsMenuProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <button
        aria-label={tr('anfragen.abbruch.menu_aria')}
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: `1px solid ${t.border}`, borderRadius: 6,
          padding: '1px 6px', cursor: 'pointer', color: t.muted,
          fontSize: 13, lineHeight: '16px', fontFamily: 'Outfit, sans-serif',
        }}
      >⋯</button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 40,
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 8,
          boxShadow: dark ? '0 6px 16px rgba(0,0,0,0.4)' : '0 6px 16px -4px rgba(16,24,40,0.14)',
          minWidth: 170, padding: 4,
        }}>
          <button
            onClick={() => { setOpen(false); onAbbrechen(); }}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: 'none', border: 'none', borderRadius: 6,
              padding: '7px 10px', cursor: 'pointer',
              fontSize: 12, fontWeight: 500, color: '#b42318',
              fontFamily: 'Outfit, sans-serif',
            }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = t.hover}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'none'}
          >
            {tr('anfragen.abbruch.action')}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Selbstständiges Control (Menü + Dialog + API-Call) ─────────────────── */

interface AnfrageAbbruchControlProps {
  dark: boolean;
  /** Numerische User-ID (PB oder BK) — der Endpoint ist user-generisch. */
  userId: number;
  /** PB- oder BK-Anfrage — bestimmt die angebotenen Abbruch-Gründe. */
  scope: AbbruchScope;
  /** Anzeigename für den Dialog-Untertitel. */
  userName?: string;
  /** Aktuelle Phase: < 0 → Anfrage ist bereits abgebrochen (zeigt Badge statt Menü). */
  phase?: number | null;
  /** Nach erfolgreichem Abbruch (Toast kommt vom Control selbst). */
  onCancelled?: (result: AnfrageAbbruchResult) => void;
}

/**
 * Ein-Zeilen-Einbau des Abbruch-Menüpunkts für Detail-/Kontoansichten:
 * rendert das ⋯-Menü + Dialog + API-Call komplett selbst. Ohne
 * users.write-Permission wird nichts gerendert; bei bereits negativer
 * Phase erscheint ein "Anfrage abgebrochen"-Badge.
 */
export function AnfrageAbbruchControl({ dark, userId, scope, userName, phase, onCancelled }: AnfrageAbbruchControlProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { hasPermission } = usePermissions();
  const [open, setOpen] = useState(false);

  if (!hasPermission('users.write')) return null;

  if (typeof phase === 'number' && phase < 0) {
    return (
      <span style={{
        fontSize: 11, fontWeight: 600, borderRadius: 6, padding: '3px 9px',
        background: dark ? 'rgba(180,35,24,0.14)' : '#fef3f2',
        color: '#b42318', border: '1px solid #b4231855', whiteSpace: 'nowrap',
      }}>
        {tr('anfragen.abbruch.cancelled_badge')}
      </span>
    );
  }

  return (
    <>
      <AnfrageActionsMenu dark={dark} onAbbrechen={() => setOpen(true)} />
      <AnfrageAbbruchDialog
        open={open}
        dark={dark}
        scope={scope}
        anfrageName={userName}
        onClose={() => setOpen(false)}
        onConfirm={async (grundId, beschreibung) => {
          const result = await v2AnfrageAbbruch.abbrechen(userId, {
            grund_id: grundId,
            ...(beschreibung ? { beschreibung } : {}),
          });
          setOpen(false);
          toast(tr('anfragen.abbruch.success'), 'success');
          onCancelled?.(result);
        }}
      />
    </>
  );
}

/* ─── Abbruch-Dialog ─────────────────────────────────────────────────────── */

interface AnfrageAbbruchDialogProps {
  open: boolean;
  dark: boolean;
  /** PB- oder BK-Anfrage — nur die Gründe dieser Rolle werden angeboten. */
  scope: AbbruchScope;
  /** Anzeigename der Anfrage (Familienname) für den Dialog-Untertitel. */
  anfrageName?: string;
  onClose: () => void;
  /** Wirft bei Fehler — der Dialog zeigt dann eine Fehlermeldung an. */
  onConfirm: (grundId: number, beschreibung: string) => Promise<void>;
}

export function AnfrageAbbruchDialog({ open, dark, scope, anfrageName, onClose, onConfirm }: AnfrageAbbruchDialogProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [gruende, setGruende] = useState<AbbruchGrund[]>([]);
  const [loading, setLoading] = useState(false);
  const [grundId, setGrundId] = useState<number | null>(null);
  const [beschreibung, setBeschreibung] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Gründe bei jedem Öffnen frisch laden + Formular zurücksetzen.
  useEffect(() => {
    if (!open) return;
    setGrundId(null);
    setBeschreibung('');
    setError(null);
    setLoading(true);
    v2AbbruchGruende.list(false, scope)
      .then(setGruende)
      .catch(() => setError(tr('anfragen.abbruch.error.load')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const selected = gruende.find(g => g.id === grundId) ?? null;
  const needsBeschreibung = !!selected?.requires_beschreibung;
  const canSubmit = grundId !== null && (!needsBeschreibung || beschreibung.trim() !== '') && !saving;

  const submit = async () => {
    if (grundId === null) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(grundId, beschreibung.trim());
    } catch {
      setError(tr('anfragen.abbruch.error.save'));
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(16,24,40,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 14,
          width: '100%', maxWidth: 440, padding: 20,
          display: 'flex', flexDirection: 'column', gap: 12,
          maxHeight: '85vh', overflowY: 'auto',
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: t.heading }}>
            {tr('anfragen.abbruch.title')}
          </div>
          <div style={{ fontSize: 12, color: t.muted, marginTop: 3 }}>
            {anfrageName
              ? tr('anfragen.abbruch.subtitle_named', { name: anfrageName })
              : tr('anfragen.abbruch.subtitle')}
          </div>
        </div>

        {loading && <div style={{ fontSize: 12, color: t.muted }}>{tr('common.loading_short')}</div>}

        {!loading && gruende.length === 0 && !error && (
          <div style={{ fontSize: 12, color: t.muted }}>{tr('anfragen.abbruch.no_gruende')}</div>
        )}

        {/* Grund-Auswahl */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {gruende.map(g => {
            const active = g.id === grundId;
            return (
              <label
                key={g.id}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${active ? '#b42318' : t.border}`,
                  background: active ? (dark ? 'rgba(180,35,24,0.12)' : '#fef3f2') : t.card,
                }}
              >
                <input
                  type="radio"
                  name="abbruch-grund"
                  checked={active}
                  onChange={() => setGrundId(g.id)}
                  style={{ marginTop: 2, accentColor: '#b42318' }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{g.titel}</span>
                  {g.beschreibung && (
                    <span style={{ fontSize: 11, color: t.muted }}>{g.beschreibung}</span>
                  )}
                </span>
              </label>
            );
          })}
        </div>

        {/* Pflicht-Beschreibung bei "Sonstiges" */}
        {needsBeschreibung && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {tr('anfragen.abbruch.beschreibung_label')}
            </div>
            <textarea
              value={beschreibung}
              onChange={e => setBeschreibung(e.target.value)}
              placeholder={tr('anfragen.abbruch.beschreibung_placeholder')}
              rows={3}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                border: `1px solid ${t.border}`, borderRadius: 8,
                padding: '8px 10px', fontSize: 13, color: t.heading,
                background: t.inputBg, fontFamily: 'Outfit, sans-serif', outline: 'none',
              }}
            />
          </div>
        )}

        {error && <div style={{ fontSize: 12, color: '#b42318' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <Button variant="secondary" size="sm" dark={dark} onClick={onClose}>
            {tr('anfragen.abbruch.cancel')}
          </Button>
          <button
            disabled={!canSubmit}
            onClick={() => void submit()}
            style={{
              background: canSubmit ? '#b42318' : t.border,
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            {saving ? tr('anfragen.abbruch.saving') : tr('anfragen.abbruch.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
